<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="clockDirty.id ? 'Edit PA Clock' : 'Add New PA Clock'"
  >
    <v-card-text class="pa-6">
      <validation-observer ref="validation">
        <v-row
          no-gutters
          class="mb-2"
        >
          <v-col class="font-weight-bold">
            Select public announcement
          </v-col>
          <v-col cols="auto">
            <v-btn
              small
              color="secondary"
              text
              @click="editLibraryFile.libraryFile = undefined; editLibraryFile.visible = true"
            >
              <v-icon
                left
                small
              >
                fa fa-plus
              </v-icon>
              New PA
            </v-btn>
          </v-col>
        </v-row>

        <v-autocomplete
          v-model="selectedLibraryFileId"
          :items="libraryFiles.data"
          item-text="title"
          item-value="id"
          outlined
          placeholder="Find public announcement or add new"
        >
          <template v-slot:item="{ item: libraryFile }">
            <v-list-item-content>
              <v-list-item-title>
                {{ libraryFile.title }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ libraryFile.description }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn
                color="secondary"
                outlined
                small
                class="text-none ma-0 pa-0"
                @click="editLibraryFile.libraryFile = libraryFile; editLibraryFile.visible = true"
              >
                Edit
              </v-btn>
            </v-list-item-action>
          </template>
        </v-autocomplete>

        <v-row align="center">
          <v-col class="font-weight-bold">
            Playback Type
          </v-col>
          <v-col cols="6">
            <v-btn-toggle
              v-model="clockDirty.playback_type"
              color="primary"
              mandatory
              class="row no-gutters"
              style="width: 100%"
            >
              <v-btn
                :value="PLAYBACK_TYPE_DEFAULT"
                large
                class="col text-none"
              >
                Default
              </v-btn>
              <v-btn
                :value="PLAYBACK_TYPE_TCP_MESSAGE"
                large
                class="col text-none"
              >
                TCP Message
              </v-btn>
            </v-btn-toggle>
          </v-col>
        </v-row>

        <div v-if="clockDirty.playback_type === PLAYBACK_TYPE_DEFAULT">
          <div class="mb-2 font-weight-bold">
            On weekdays
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-btn-toggle
              v-model="clockDirty.days"
              multiple
              dense
              class="row no-gutters mb-2"
              color="primary"
              style="width: 100%"
            >
              <v-btn
                v-for="day in days"
                :key="day.day"
                :value="day.day"
                large
                class="col caption text-none"
              >
                {{ day.name }}
              </v-btn>
            </v-btn-toggle>
            <div class="v-text-field__details mb-2">
              <div class="v-messages theme--light error--text">
                <div class="v-messages__wrapper">
                  {{ errors[0] }}
                </div>
              </div>
            </div>
          </validation-provider>

          <div class="font-weight-bold mb-2">
            Play once at
          </div>
          <v-row no-gutters>
            <v-col
              class="pr-2"
              cols="4"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required|time"
              >
                <v-text-field
                  v-model="clockDirty.start_time_at"
                  v-mask="'##:##'"
                  type="tel"
                  outlined
                  clearable
                  placeholder="00:00"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </div>

        <div v-if="clockDirty.playback_type === PLAYBACK_TYPE_TCP_MESSAGE">
          <div class="font-weight-bold mb-2">
            TCP message content
          </div>
          <v-row no-gutters>
            <v-col>
              <validation-provider
                v-slot="{ errors }"
                rules="required|alpha_num"
              >
                <v-text-field
                  v-model="clockDirty.playback_tcp_message"
                  outlined
                  clearable
                  placeholder="03010000000100000000000000000005"
                  :error-messages="errors"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="clockDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteClock()"
    />

    <edit-pa-dialog
      :visible.sync="editLibraryFile.visible"
      :library-file="editLibraryFile.libraryFile"
      :client="radio.client"
      @pa:added="fetchLibraryFiles(); selectedLibraryFileId = $event.id"
      @pa:updated="fetchLibraryFiles()"
      @pa:deleted="fetchLibraryFiles()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import ClockModel from '@/models/ClockModel'
import LibraryFiles from '@/services/LibraryFiles'
import CardDialog from '@/components/helpers/CardDialog'
import Radios from '@/services/Radios'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import EditPaDialog from '@/components/pa/EditPaDialog'
import LibraryFileModel from '@/models/LibraryFileModel'

export default {
  name: 'EditPaClockDialog',
  components: { EditPaDialog, ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    clock: {
      type: Object,
      default: () => cloneDeep(ClockModel.emptyModel)
    },
    radio: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      clockDirty: cloneDeep(this.clock),
      libraryFiles: {
        loading: false,
        data: []
      },
      selectedLibraryFileId: undefined,
      editLibraryFile: {
        libraryFile: undefined,
        visible: false
      },
      submitting: false,
      deleting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedLibraryFile() {
      if (!this.selectedLibraryFileId) return undefined

      return this.libraryFiles.data.find(p => p.id === this.selectedLibraryFileId)
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      this.clockDirty = cloneDeep(this.clock)
      if (this.$refs.validation) this.$refs.validation.reset()

      if (this.clock.id && this.clock.individual_files.length > 0) {
        this.selectedLibraryFileId = this.clock.individual_files[0].id
      }
    }
  },
  created() {
    this.days = ClockModel.days
    this.PLAYBACK_TYPE_DEFAULT = ClockModel.PLAYBACK_TYPE_DEFAULT
    this.PLAYBACK_TYPE_AFTER_SONGS = ClockModel.PLAYBACK_TYPE_AFTER_SONGS
    this.PLAYBACK_TYPE_AFTER_MINUTES = ClockModel.PLAYBACK_TYPE_AFTER_MINUTES
    this.PLAYBACK_TYPE_TCP_MESSAGE = ClockModel.PLAYBACK_TYPE_TCP_MESSAGE

    this.fetchLibraryFiles()
  },
  methods: {
    async fetchLibraryFiles() {
      try {
        this.libraryFiles.loading = true

        this.libraryFiles.data = await LibraryFiles.fetch({
          type: LibraryFileModel.TYPE_PA,
          client_id: this.radio.client.id
        })
      } catch (e) {

      } finally {
        this.libraryFiles.loading = false
      }
    },
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      this.submitting = true

      try {
        const data = {
          content_type: ClockModel.CONTENT_TYPE_PA,
          individual_files: [this.selectedLibraryFileId],
          playback_type: this.clockDirty.playback_type
        }

        if (data.playback_type === ClockModel.PLAYBACK_TYPE_TCP_MESSAGE) {
          data.playback_tcp_message = this.clockDirty.playback_tcp_message
        } else {
          data.start_time_at = this.$dayjs(this.clockDirty.start_time_at, 'HH:mm').format('HH:mm:ss')
          data.days = this.clockDirty.days
        }

        if (this.clockDirty.id) {
          const result = await Radios.updateClock(this.radio.id, this.clockDirty.id, data)

          this.$emit('clock:updated', result)

          this.$store.dispatch('alert', {
            text: 'Clock updated',
            color: 'success'
          })
        } else {
          const result = await Radios.storeClock(this.radio.id, data)

          this.$emit('clock:added', result)

          this.$store.dispatch('alert', {
            text: 'New clock successfully added',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting clock',
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteClock() {
      this.deleting = true

      try {
        const result = await Radios.deleteClock(this.radio.id, this.clockDirty.id)

        this.$store.dispatch('alert', {
          text: 'Clock deleted',
          color: 'success'
        })

        this.modalVisible = false
        this.$emit('clock:deleted', result)
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    }
  }
}
</script>
